import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { sleep } from "../timers/timers";

export type RetryConfig = {
  start: number;
  sleep: number;
  scale: number;
  maxSleep: number;
};

const retryInitial = 2e3; // 2s
const retryScaleOff = 1.5;
const retryMaxInterval = 30e3; // 30s
const retryMaxDuration = 30 * 60e3; // wait 30 mins

function request(
  this: AxiosInstance,
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  // Requests re-issued by the response interceptor carry their retry state
  // (elapsed-time budget, scaled backoff). Overwriting it here would reset
  // the 30-minute cap and pin the backoff at its initial value on every
  // attempt, turning the 202/429 polling loop into an unbounded fixed-rate
  // hammer.
  if (config.retry) {
    return config;
  }
  return {
    ...config,
    retry: {
      start: Date.now(),
      sleep: retryInitial,
      maxSleep: retryMaxInterval,
      scale: retryScaleOff,
    },
  };
}

async function response(
  this: AxiosInstance,
  response: AxiosResponse,
): Promise<AxiosResponse> {
  if (response.status !== 202 && response.status !== 429) {
    return response;
  }
  const retry = response.config.retry;
  if (!retry) {
    return response;
  }
  if (Date.now() - retry.start > retryMaxDuration) {
    return response;
  }
  const retryAfter = Number(response.headers["retry-after"] ?? 0);
  if (retryAfter > 0) {
    retry.sleep = Math.max(retry.sleep, retryAfter * 1e3);
  }
  const maxSleepAfter = Number(response.headers["x-max-retry-after"] ?? 0);
  if (maxSleepAfter > 0) {
    retry.maxSleep = maxSleepAfter * 1e3;
  }

  await sleep(Math.min(retry.sleep, retry.maxSleep), response.config.signal);

  retry.sleep *= retry.scale;

  return await (this ?? axios).request({
    ...(response.config.originalConfig ?? response.config),
    originalConfig: response.config.originalConfig,
    retry,
  });
}

async function error(
  this: AxiosInstance,
  error: unknown,
): Promise<AxiosResponse> {
  if (!axios.isAxiosError(error)) {
    throw error;
  }
  const response = error.response;
  if (response && response.status === 429 && response.config.retry) {
    const newresponse = await retry.response.call(this, response);
    if (newresponse === response) {
      throw error;
    }
    return newresponse;
  }
  throw error;
}

export const retry = {
  request,
  response,
  error,
};
